import React, { useState, useEffect } from 'react';
import './RoleAssignment.css';
import { API_ENDPOINTS } from '../config/api';

const RoleAssignment = () => {
  const [roles, setRoles] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [akademisyenler, setAkademisyenler] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [selectedAkademisyen, setSelectedAkademisyen] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');

  // Dekan uyarı modal state
  const [showDeanWarning, setShowDeanWarning] = useState(false);
  const [existingDean, setExistingDean] = useState(null);
  const [showChairWarning, setShowChairWarning] = useState(false);
  const [existingChair, setExistingChair] = useState(null);
  const [pendingAssignment, setPendingAssignment] = useState(null);

  // Rol bazlı kontroller
  const selectedRoleObj = roles.find(r => r.id.toString() === selectedRole.toString());
  const isProgramChair = selectedRoleObj?.name === 'Program Başkanı';
  const isDeanOrAssistant = selectedRoleObj?.name === 'Dekan' || selectedRoleObj?.name === 'Dekan Yardımcısı';
  const needsFaculty = isProgramChair || isDeanOrAssistant;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedFaculty) {
      fetchPrograms(selectedFaculty);
    } else {
      setPrograms([]);
      setSelectedProgram('');
    }
  }, [selectedFaculty]);

  const fetchPrograms = async (facultyId) => {
    setProgramsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ROLES_PROGRAMS(facultyId));
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setProgramsLoading(false);
    }
  };



  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [rolesRes, facultiesRes, akademisyenRes, assignmentsRes] = await Promise.all([
        fetch(API_ENDPOINTS.ROLES_TYPES),
        fetch(API_ENDPOINTS.ROLES_FACULTIES),
        fetch(API_ENDPOINTS.ROLES_AKADEMISYEN),
        fetch(API_ENDPOINTS.ROLES_ASSIGNMENTS)
      ]);

      const [rolesData, facultiesData, akademisyenData, assignmentsData] = await Promise.all([
        rolesRes.json(),
        facultiesRes.json(),
        akademisyenRes.json(),
        assignmentsRes.json()
      ]);

      if (rolesRes.ok) setRoles(rolesData);
      if (facultiesRes.ok) setFaculties(facultiesData);
      if (akademisyenRes.ok) setAkademisyenler(akademisyenData);
      if (assignmentsRes.ok) setAssignments(assignmentsData);

    } catch (err) {
      setError('Veriler yüklenirken hata oluştu.');
      console.error('Data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedAkademisyen || !selectedRole) {
      setError('Akademisyen ve rol seçimi zorunludur.');
      return;
    }

    // Dekan ve Dekan Yardımcısı için fakülte seçimi zorunlu
    if (isDeanOrAssistant && !selectedFaculty) {
      setError('Dekan ve Dekan Yardımcısı için fakülte seçimi zorunludur.');
      return;
    }

    // Program Başkanı için program seçimi zorunlu
    if (isProgramChair && !selectedProgram) {
      setError('Program Başkanı için program seçimi zorunludur.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.ROLES_ASSIGN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          akademisyen_id: parseInt(selectedAkademisyen),
          role_type_id: parseInt(selectedRole),
          faculty_id: selectedFaculty ? parseInt(selectedFaculty) : null,
          program_id: selectedProgram ? parseInt(selectedProgram) : null,
          assigned_by: 1 // Admin ID - session'dan alınabilir
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Rol başarıyla atandı.');
        setSelectedAkademisyen('');
        setSelectedRole('');
        setSelectedFaculty('');
        setSelectedProgram('');
        fetchData(); // Listeyi yenile
      } else {
        // Eğer dekan için çakışma varsa, uyarı modalını göster
        if (data.existing_dean && isDeanOrAssistant && selectedRoleObj?.name === 'Dekan') {
          setExistingDean(data.existing_dean);
          setPendingAssignment({
            akademisyen_id: parseInt(selectedAkademisyen),
            role_type_id: parseInt(selectedRole),
            faculty_id: parseInt(selectedFaculty),
            assigned_by: 1
          });
          setShowDeanWarning(true);
        } else if (data.existing_chair && isProgramChair) {
          setExistingChair(data.existing_chair);
          setPendingAssignment({
            akademisyen_id: parseInt(selectedAkademisyen),
            role_type_id: parseInt(selectedRole),
            faculty_id: parseInt(selectedFaculty),
            program_id: parseInt(selectedProgram),
            assigned_by: 1
          });
          setShowChairWarning(true);
        } else {
          setError(data.error || 'Rol atama başarısız.');
        }
      }
    } catch (err) {
      setError('Sunucu hatası oluştu.');
      console.error('Role assignment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (assignmentId) => {
    if (!window.confirm('Bu rol atamasını deaktif etmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ROLES_DEACTIVATE(assignmentId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Rol ataması deaktif edildi.');
        fetchData(); // Listeyi yenile
      } else {
        setError(data.error || 'Rol deaktif etme başarısız.');
      }
    } catch (err) {
      setError('Sunucu hatası oluştu.');
      console.error('Role deactivation error:', err);
    }
  };

  // Mevcut dekanı pasifleştir ve yeni dekan ata
  const handleReplaceExistingDean = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Önce mevcut dekanı deaktif et
      const deactivateResponse = await fetch(API_ENDPOINTS.ROLES_DEACTIVATE(existingDean.id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!deactivateResponse.ok) {
        const deactivateData = await deactivateResponse.json();
        throw new Error(deactivateData.error || 'Mevcut dekan deaktif edilemedi.');
      }

      // Sonra yeni dekan ata
      const assignResponse = await fetch(API_ENDPOINTS.ROLES_ASSIGN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pendingAssignment),
      });

      const assignData = await assignResponse.json();

      if (assignResponse.ok) {
        setSuccess('Mevcut dekan pasifleştirildi ve yeni dekan başarıyla atandı.');
        setSelectedAkademisyen('');
        setSelectedRole('');
        setSelectedFaculty('');
        setSelectedProgram('');
        setShowDeanWarning(false);
        setExistingDean(null);
        setPendingAssignment(null);
        fetchData(); // Listeyi yenile
      } else {
        throw new Error(assignData.error || 'Yeni dekan ataması başarısız.');
      }
    } catch (err) {
      setError(err.message || 'İşlem sırasında hata oluştu.');
      console.error('Dean replacement error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Mevcut program başkanını pasifleştir ve yeni başkan ata
  const handleReplaceExistingChair = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Önce mevcut başkanı deaktif et
      const deactivateResponse = await fetch(API_ENDPOINTS.ROLES_DEACTIVATE(existingChair.id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!deactivateResponse.ok) {
        const deactivateData = await deactivateResponse.json();
        throw new Error(deactivateData.error || 'Mevcut program başkanı deaktif edilemedi.');
      }

      // Sonra yeni başkan ata
      const assignResponse = await fetch(API_ENDPOINTS.ROLES_ASSIGN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pendingAssignment),
      });

      const assignData = await assignResponse.json();

      if (assignResponse.ok) {
        setSuccess('Mevcut program başkanı pasifleştirildi ve yeni başkan başarıyla atandı.');
        setSelectedAkademisyen('');
        setSelectedRole('');
        setSelectedFaculty('');
        setSelectedProgram('');
        setShowChairWarning(false);
        setExistingChair(null);
        setPendingAssignment(null);
        fetchData(); // Listeyi yenile
      } else {
        throw new Error(assignData.error || 'Yeni program başkanı ataması başarısız.');
      }
    } catch (err) {
      setError(err.message || 'İşlem sırasında hata oluştu.');
      console.error('Program chair replacement error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Dekan uyarı modalını kapat
  const handleCloseDeanWarning = () => {
    setShowDeanWarning(false);
    setExistingDean(null);
    setPendingAssignment(null);
  };

  // Program başkanı uyarı modalını kapat
  const handleCloseChairWarning = () => {
    setShowChairWarning(false);
    setExistingChair(null);
    setPendingAssignment(null);
  };



  return (
    <div className="role-assignment-container">
      <h2 className="role-assignment-title">
        Rol Tanımla
      </h2>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Rol Atama Formu */}
      <div className="role-form-container">
        <h3 className="role-form-title">
          Yeni Rol Ataması
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Akademisyen Seçimi */}
            <div className="form-group">
              <label className="form-label">
                Akademisyen *
              </label>
              <select
                value={selectedAkademisyen}
                onChange={(e) => setSelectedAkademisyen(e.target.value)}
                className="form-select"
                required
              >
                <option value="">Akademisyen seçin</option>
                {akademisyenler
                  .filter(akademisyen => akademisyen.academician && akademisyen.academician.trim() !== '')
                  .map(akademisyen => (
                    <option key={akademisyen.id} value={akademisyen.id}>
                      {akademisyen.academician || akademisyen.email}
                    </option>
                  ))}
              </select>
            </div>

            {/* Rol Seçimi */}
            <div className="form-group">
              <label className="form-label">
                Rol *
              </label>
              <select
                value={selectedRole}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedRole(val);
                  const newRoleObj = roles.find(r => r.id.toString() === val.toString());
                  const isChair = newRoleObj?.name === 'Program Başkanı';
                  const isDeanOrAsst = newRoleObj?.name === 'Dekan' || newRoleObj?.name === 'Dekan Yardımcısı';
                  
                  if (!isChair && !isDeanOrAsst) {
                    setSelectedFaculty('');
                    setSelectedProgram('');
                  }
                }}
                className="form-select"
                required
              >
                <option value="">Rol seçin</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Fakülte Seçimi (Dekan, Dekan Yardımcısı ve Program Başkanı için) */}
          {needsFaculty && (
            <div className="form-group">
              <label className="form-label">
                Fakülte *
              </label>
              <select
                value={selectedFaculty}
                onChange={(e) => setSelectedFaculty(e.target.value)}
                className="form-select"
                required
              >
                <option value="">Fakülte seçin</option>
                {faculties.map(faculty => (
                  <option key={faculty.id} value={faculty.id}>
                    {faculty.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Program Seçimi (Sadece Program Başkanı için) */}
          {isProgramChair && selectedFaculty && (
            <div className="form-group">
              <label className="form-label">
                Program *
              </label>
              <select
                value={selectedProgram}
                onChange={(e) => setSelectedProgram(e.target.value)}
                className="form-select"
                required
                disabled={programsLoading}
              >
                <option value="">{programsLoading ? 'Programlar yükleniyor...' : 'Program seçin'}</option>
                {programs.map(program => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? 'İşleniyor...' : 'Rol Ata'}
          </button>
        </form>
      </div>

      {/* Aktif Rol Atamaları */}
      <div className="assignments-container">
        <h3 className="assignments-title">
          Aktif Rol Atamaları
        </h3>

        {loading ? (
          <div className="loading-container">
            Yükleniyor...
          </div>
        ) : assignments.filter(a => a.is_active).length === 0 ? (
          <div className="empty-container">
            Henüz aktif rol ataması bulunmamaktadır.
          </div>
        ) : (
          <div className="assignments-table-container" style={{ overflowX: 'auto' }}>
            <table className="assignments-table">
              <thead>
                <tr>
                  <th>Akademisyen</th>
                  <th>Rol</th>
                  <th>Fakülte / Program</th>
                  <th>Durum</th>
                  <th>Atama Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {assignments.filter(assignment => assignment.is_active).map((assignment, index) => (
                  <tr key={assignment.id}>
                    <td>{assignment.akademisyen_name} {assignment.akademisyen_surname}</td>
                    <td>{assignment.role_name}</td>
                    <td>
                      {assignment.faculty_name && (
                        <div>{assignment.faculty_name}</div>
                      )}
                      {assignment.program_name && (
                        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                          ↳ {assignment.program_name}
                        </div>
                      )}
                      {!assignment.faculty_name && !assignment.program_name && (
                        <span style={{ color: '#6b7280' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className="status-badge status-active">
                        Aktif
                      </span>
                    </td>
                    <td className="date-info">
                      {new Date(assignment.assigned_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeactivate(assignment.id)}
                        className="deactivate-button"
                      >
                        Deaktif Et
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pasif Rol Atamaları */}
      {assignments.filter(a => !a.is_active).length > 0 && (
        <div className="assignments-container">
          <h3 className="assignments-title">
            Pasif Rol Atamaları
          </h3>

          <div className="assignments-table-container" style={{ overflowX: 'auto' }}>
            <table className="assignments-table">
              <thead>
                <tr>
                  <th>Akademisyen</th>
                  <th>Rol</th>
                  <th>Fakülte / Program</th>
                  <th>Durum</th>
                  <th>Atama Tarihi</th>
                  <th>Pasifleştirme Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {assignments.filter(assignment => !assignment.is_active).map((assignment, index) => (
                  <tr key={assignment.id}>
                    <td>{assignment.akademisyen_name} {assignment.akademisyen_surname}</td>
                    <td>{assignment.role_name}</td>
                    <td>
                      {assignment.faculty_name && (
                        <div>{assignment.faculty_name}</div>
                      )}
                      {assignment.program_name && (
                        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                          ↳ {assignment.program_name}
                        </div>
                      )}
                      {!assignment.faculty_name && !assignment.program_name && (
                        <span style={{ color: '#6b7280' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className="status-badge status-inactive">
                        Pasif
                      </span>
                    </td>
                    <td className="date-info">
                      {new Date(assignment.assigned_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="date-info">
                      {assignment.deactivated_at ? 
                        new Date(assignment.deactivated_at).toLocaleDateString('tr-TR') : 
                        '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dekan Uyarı Modalı */}
      {showDeanWarning && existingDean && (
        <div className="modal-overlay" onClick={handleCloseDeanWarning}>
          <div className="modal-content dean-warning-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={handleCloseDeanWarning}>
              &times;
            </button>
            
            <div className="dean-warning-header">
              <h3 className="dean-warning-title">⚠️ Dekan Uyarısı</h3>
            </div>

            <div className="dean-warning-content">
              <p className="dean-warning-message">
                Bu fakülte için zaten aktif bir dekan bulunmaktadır:
              </p>
              
              <div className="existing-dean-info">
                <strong>{existingDean.name}</strong>
              </div>

              <p className="dean-warning-instruction">
                Yeni dekan atayabilmek için önce mevcut dekanın pasifleştirilmesi gerekmektedir.
              </p>
            </div>

            <div className="dean-warning-actions">
              <button 
                className="btn-cancel" 
                onClick={handleCloseDeanWarning}
                disabled={loading}
              >
                İptal
              </button>
              <button 
                className="btn-replace-dean" 
                onClick={handleReplaceExistingDean}
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : 'Mevcut Dekanı Pasifleştir ve Yeni Dekan Ata'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Program Başkanı Uyarı Modalı */}
      {showChairWarning && existingChair && (
        <div className="modal-overlay" onClick={handleCloseChairWarning}>
          <div className="modal-content dean-warning-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={handleCloseChairWarning}>
              &times;
            </button>
            
            <div className="dean-warning-header">
              <h3 className="dean-warning-title">⚠️ Program Başkanı Uyarısı</h3>
            </div>

            <div className="dean-warning-content">
              <p className="dean-warning-message">
                Bu program için zaten aktif bir program başkanı bulunmaktadır:
              </p>
              
              <div className="existing-dean-info">
                <strong>{existingChair.name}</strong>
              </div>

              <p className="dean-warning-instruction">
                Yeni program başkanı atayabilmek için önce mevcut başkanın pasifleştirilmesi gerekmektedir.
              </p>
            </div>

            <div className="dean-warning-actions">
              <button 
                className="btn-cancel" 
                onClick={handleCloseChairWarning}
                disabled={loading}
              >
                İptal
              </button>
              <button 
                className="btn-replace-dean" 
                onClick={handleReplaceExistingChair}
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : 'Mevcut Başkanı Pasifleştir ve Yeni Başkan Ata'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleAssignment;
